import EmptyState from '@/components/empty-state';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateTime } from '@/lib/format';

export interface DeveloperSchema {
  version: number;
  sql: string;
  appliedAt: string | null;
  tables: { name: string; listKey: string; policy: string }[];
}

interface SchemaTabProps {
  schema: DeveloperSchema | null;
}

const SchemaTab = ({ schema }: SchemaTabProps) => {
  if (!schema) {
    return (
      <Card>
        <CardContent>
          <EmptyState
            title="No schema is applied yet."
            description="The schema is generated from the design once the app has a list and its database is provisioned."
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>
            <h2>Tables</h2>
          </CardTitle>
          <CardDescription>
            Schema version {schema.version}
            {schema.appliedAt ? `, applied ${formatDateTime(schema.appliedAt)}` : ', not applied'}.
            The template gives every table row-level security and one policy scoped to this app.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {schema.tables.length === 0 ? (
            <EmptyState description="This schema version has no tables." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Table</TableHead>
                  <TableHead>List key</TableHead>
                  <TableHead>Policy</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schema.tables.map((table) => (
                  <TableRow key={table.name}>
                    <TableCell>
                      <code className="text-xs">{table.name}</code>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs">{table.listKey}</code>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs">{table.policy}</code>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>Generated SQL</h2>
          </CardTitle>
          <CardDescription>Generated by the access-rule template. Not hand-edited.</CardDescription>
        </CardHeader>
        <CardContent>
          <pre
            tabIndex={0}
            aria-label="Generated SQL"
            className="bg-muted max-h-96 overflow-auto rounded-md p-3 text-xs"
          >
            {schema.sql}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
};

export default SchemaTab;
